import './App.css';
import React, { Suspense, useEffect, useState } from 'react';
import {
  Route,
  Routes,
  useParams,
  useNavigate,
  useLocation,
  Outlet,
  Navigate,
} from 'react-router-dom';
import SetPlan from './components/Component/SetPlan';
import PricingPage from './components/pages/PricingPage/PricingPage.js';
import HomePage from './components/pages/HomePage';
import PaymentSuccess from './components/Component/PaymentSuccess';
import useProjectStore from './components/ProjectCanvas/useProjectStore';
import WorkflowCanvas from './components/pages/Canvas/WorkflowCanvas';
import WorkflowTestPage from './components/pages/WorkflowTestPage';
import WorkflowTestResultPage from './components/pages/WorkflowTestResultPage';
import WorkflowProgressView from './components/pages/WorkflowProgressView/WorkflowProgressView';
import { QueryParamProvider } from 'use-query-params';
import { ReactRouter6Adapter } from 'use-query-params/adapters/react-router-6';
import setPageTitle from './utils/setPageTitle';
import ExpandedTaskPage from './components/Task/ExpandedTaskPage';
import componentRoutes from './utils/componentRoutes.js';
import WorkflowSchema from './components/WorkflowSchema/WorkflowSchema';

import { AI_AGENTS_CLIENT_IDS, AI_CASES_PLAYBOOKS_CLIENT_IDS } from './constants/featureFlags';

const ConditionalOrganizationRoute = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const billingInitialized = useProjectStore(s => s.billingInitialized);
  const isV2Billing = useProjectStore(s => s.isV2Billing);

  if (!billingInitialized) {
    return <div className="loadingGif" />;
  }

  if (location.pathname === '/organization-legacy' && isV2Billing) {
    return <Navigate to="/organization" replace />;
  }

  if (location.pathname === '/organization' && !isV2Billing) {
    return <Navigate to="/organization-legacy" replace />;
  }

  if (isV2Billing) {
    return <OrganizationV2 />;
  } else {
    return <Organization />;
  }
};

const ConditionalUserManagementRoute = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const billingInitialized = useProjectStore(s => s.billingInitialized);
  const isV2Billing = useProjectStore(s => s.isV2Billing);

  if (!billingInitialized) {
    return <div className="loadingGif" />;
  }

  if (location.pathname === '/user_management' && isV2Billing) {
    return <Navigate to="/user_management_new" replace />;
  }

  if (location.pathname === '/user_management_new' && !isV2Billing) {
    return <Navigate to="/user_management" replace />;
  }

  if (isV2Billing) {
    return <UserManagement />;
  } else {
    return <UserTemplate />;
  }
};

const AppRoutes = props => {
  let location = useLocation();
  const { firstLogin } = props;
  const clientId = useProjectStore(s => s.clientId);

  const queryParams = new URLSearchParams(location.search);
  useEffect(() => {
    setPageTitle(location);
  }, [location]);

  return (
    <Suspense fallback={<div className="loadingGif" />}>
        <QueryParamProvider adapter={ReactRouter6Adapter}>
            <Routes>
            {/* APP PAGES ROUTES */}
            <Route path="/node-ui-preview" element={<NodeUIPreviewPage />} />
            <Route path="/" element={<HomePage />} />
              <Route path="/legacy-home" element={<HomePage />} />
              <Route path="/project/folder/:id" element={<InsideFolderPage />} />
              <Route path="/project" element={<WorkflowPage />} />
              <Route path="/project/trash" element={<TrashPage />} />
              <Route path="/credential_store" element={<CredentialStore />} />
              <Route path="/credential_store/:credential_id" element={<CredentialStore />} />
              <Route path="/custom_functions" element={<CustomFunctionsPage />} />
              <Route path="/custom_functions/:functionId" element={<CustomFunctionsPage />} />
              <Route path={'/mcp'} element={<MCPDocumentationPage />} />
              <Route path={'/agent/form/:formInstanceId'} element={<AgentFormInstancePage />} />
              <Route
                path="/channels"
                element={
                  AI_AGENTS_CLIENT_IDS.includes(clientId) ? (
                    <AgentChannelsHomePage />
                  ) : (
                    <Navigate to="/" replace />
                  )
                }
              />
              <Route
                path="/cases"
                element={
                  AI_CASES_PLAYBOOKS_CLIENT_IDS.includes(clientId) ? (
                    <CasesListPage />
                  ) : (
                    <Navigate to="/" replace />
                  )
                }
              />
              <Route
                path="/cases/:caseId"
                element={
                  AI_CASES_PLAYBOOKS_CLIENT_IDS.includes(clientId) ? (
                    <CaseDetailPage />
                  ) : (
                    <Navigate to="/" replace />
                  )
                }
              />
              <Route
                path="/playbooks"
                element={
                  AI_CASES_PLAYBOOKS_CLIENT_IDS.includes(clientId) ? (
                    <PlaybooksListPage />
                  ) : (
                    <Navigate to="/" replace />
                  )
                }
              />
              <Route
                path="/playbooks/:playbookId"
                element={
                  AI_CASES_PLAYBOOKS_CLIENT_IDS.includes(clientId) ? (
                    <PlaybookEditorPage />
                  ) : (
                    <Navigate to="/" replace />
                  )
                }
              />
              <Route path={'/agents/chats'} element={<Navigate to="/channels" replace />} />
              <Route path={'/agents'} element={<AgentsPage />} />
              <Route path={'/agents/:agentId'} element={<AgentConfigPage />} />
              <Route path={'/task'} exact element={<TaskInboxPage />} />
              <Route path={'/taskinbox'} element={<Navigate to="/task?tab=agent" replace />} />
              <Route path={'/task_expanded'} element={<ExpandedTaskPage />} />
              <Route path={'/user_management'} element={<ConditionalUserManagementRoute />} />
              <Route path={'/user_management_new'} element={<ConditionalUserManagementRoute />} />
              <Route path="/resources" element={<ResourcesPage />} />
              <Route path="/databases" element={<DatabasesPage />} />
              <Route path="/schedules" element={<SchedulesPage />} />
              <Route path="/files" element={<FilesPage />} />
              <Route path="/global_variables" element={<GlobalVariablesPage />} />
              <Route
                path="/databases/database_management/:id"
                element={<DatabaseManagement navigate={useNavigate()} queryParams={queryParams} />}
              />
              <Route path={'/profile'} element={<ProfilePage />} />
              // This is here for v1 pricing
              <Route path="/set_plan" element={<SetPlan firstLogin={firstLogin} />} />
              // This is current pricing
              <Route path={'/pricing'} element={<PricingPage />} />
              <Route path="/payment_success" element={<PaymentSuccess />} /> {/* Kept for logged-in redirect */}
              {/* Conditional routing for organization */}
              <Route path={'/organization-legacy'} element={<ConditionalOrganizationRoute />} />
              <Route path={'/organization'} element={<ConditionalOrganizationRoute />} />
              {/* WORKFLOW CANVAS ROUTES */}
              <Route path="/project_data/:project_id" element={<ProjectDataPage />} />
              <Route path={`/project/logs/:id/:project_session_id`} element={<LogSession />} />
              <Route path={`/project/logs/:id`} element={<ProjectLog />} />
              <Route path={`/project/unsubmittedForms/:id`} element={<ProjectLog />} />
              <Route
                path={`/project/logs/progress_view/:id/:project_session_id`}
                element={<WorkflowProgressView />}
              />
              <Route path={'/project/version_history/:id'} element={<VersionHistory />} />
              <Route
                path={'/project/view_canvas/:id&version=:version'}
                element={<VersionCanvasView />}
              />
              <Route path={'/project/production/:id'} element={<ProductionCanvasView />} />
              <Route path={'/project/workflow_schema/:id'} element={<WorkflowSchema />} />
              <Route path={'/project/schedules/:id'} element={<WorkflowSchedules />} />
              <Route path={'/project/error_handling/:id'} element={<ErrorHandling />} />
              <Route path={`/project/file_library/:id`} element={<FileLibrary />} />
              <Route path={'/project/canvas/'} element={<Outlet />}>
                {/*This is project canvas main route*/}
                <Route path={`:id`} element={<WorkflowCanvas />} />
                {/*This is legacy handler for full page components*/}
                {componentRoutes.map((path, index) => (
                  <Route key={index} path={`:id/${path}`} element={<RedirectWithParams />} />
                ))}
              </Route>
              <Route
                path={`test_workflow/:project_id/:component_id`}
                element={<WorkflowTestPage />}
              />
              <Route
                path={`test_workflow/test_result/:test_id/:session_id`}
                element={<WorkflowTestResultPage />}
              />
            </Routes>
        </QueryParamProvider>
    </Suspense>
  );
};

export default AppRoutes;

// This function enables the use of PARAMS, NAVIGATION (instead of history.push) and LOCATION props in a class-based react components,
// To use it just wrap withParams(**class-react-component-name**); function around the outer most export
// Use react-router-dom hooks on function-based components instead
export function withParams(Component) {
  const WithParamsComponent = props => (
    <Component {...props} params={useParams()} navigate={useNavigate()} location={useLocation()} />
  );

  WithParamsComponent.displayName = `withParams(${Component.displayName || Component.name})`;

  return WithParamsComponent;
}

//This is a redirect handler for old component routes
const RedirectWithParams = () => {
  const { id, component_id } = useParams();
  const targetId = component_id;
  const targetPath = `/project/canvas/${id}?view=${targetId}`;

  return <Navigate to={targetPath} replace />;
};

//Lazy imports
const WorkflowPage = React.lazy(() => import('./components/pages/WorkflowPage'));
const InsideFolderPage = React.lazy(() => import('./components/pages/InsideFolderPage'));
const TrashPage = React.lazy(() => import('./components/pages/TrashPage'));
const DatabasesPage = React.lazy(() => import('./components/pages/DatabasesPage'));
const DatabaseManagement = React.lazy(() =>
  import('./components/pages/DatabasesComponents/DatabaseManagement')
);
const FileLibrary = React.lazy(() => import('./components/FileLibrary/FileLibrary'));
const VersionHistory = React.lazy(() => import('./components/VersionHistory/VersionHistory'));
const VersionCanvasView = React.lazy(() => import('./components/VersionHistory/VersionCanvasView'));
const ProductionCanvasView = React.lazy(() =>
  import('./components/pages/Canvas/ProductionCanvasView')
);
const ProjectLog = React.lazy(() => import('./components/pages/ProjectLog/ProjectLog'));
const LogSession = React.lazy(() => import('./components/pages/ProjectLog/LogSession'));
const ProfilePage = React.lazy(() => import('./components/Profile/ProfilePage'));
const UserTemplate = React.lazy(() => import('./components/User/UserTemplate'));
const UserManagement = React.lazy(() => import('./components/UserManagement/UserManagement'));
const Organization = React.lazy(() => import('./components/Organization/OrganizationSettings'));
const OrganizationV2 = React.lazy(() => import('./components/Organization/OrganizationSettingsV2'));

const ProjectDataPage = React.lazy(() => import('./components/pages/ProjectData'));
const CredentialStore = React.lazy(() =>
  import('./components/CredentialStore/CredentialStorePage')
);
const CustomFunctionsPage = React.lazy(() =>
  import('./components/CustomFunctions/CustomFunctionsPage')
);
// Kept for rollback — set UNIFIED_TASK_PAGE=false in Routes to revert
// const TaskPage = React.lazy(() => import('./components/Task/TaskPage'));
const TaskInboxPage = React.lazy(() => import('./components/TaskInbox/TaskInboxPage'));
const MCPDocumentationPage = React.lazy(() => import('./components/pages/MCPDocumentationPage'));
const WorkflowSchedules = React.lazy(() =>
  import('./components/pages/WorkflowSchedules/WorkflowSchedules')
);
const ErrorHandling = React.lazy(() =>
  import('./components/pages/ErrorHandling/ErrorHandling')
);
const ResourcesPage = React.lazy(() => import('./components/pages/ResourcesPage'));
const SchedulesPage = React.lazy(() => import('./components/pages/SchedulesPage/SchedulesPage'));
const FilesPage = React.lazy(() => import('./components/pages/FilesPage/FilesPage'));
const GlobalVariablesPage = React.lazy(() =>
  import('./components/GlobalVariables/GlobalVariablesPage')
);
const AgentsPage = React.lazy(() => import('./components/pages/AgentsPage'));
const AgentConfigPage = React.lazy(() => import('./components/pages/AgentConfigPage'));
const AgentChannelsHomePage = React.lazy(() =>
  import('./components/pages/AgentChannelsHomePage')
);
const PlaybooksListPage = React.lazy(() =>
  import('./components/pages/playbooks/PlaybooksListPage')
);
const PlaybookEditorPage = React.lazy(() =>
  import('./components/pages/playbooks/PlaybookEditorPage')
);
const CasesListPage = React.lazy(() =>
  import('./components/pages/cases/CasesListPage')
);
const CaseDetailPage = React.lazy(() =>
  import('./components/pages/cases/CaseDetailPage')
);
const AgentFormInstancePage = React.lazy(() =>
  import('./components/pages/AgentFormInstancePage')
);
const NodeUIPreviewPage = React.lazy(() =>
  import('./components/NodeUI/NodeUIPreviewPage')
);